<template>
  <div :style="`height:${chartHeight}`">
    <apexchart
      type="line"
      height="100%"
      :options="chartOptions"
      :series="chartSeries"
    />
  </div>
</template>

<script>
import moment from "moment";

export default {
  props: {
    managementHistoryData: {
      type : Array,
      required: false,
      default: () => []
    },
    loadingData: {
      type : Boolean,
      required: false,
      default: false
    },
    chartHeight: {
      type : String,
      required: false,
      default: 350
    },
    sectorName: {
      type : String,
      required: false,
      default: ""
    },
    sensorHistoryData: {
      type : Array,
      required: false,
      default: () => []
    }
  },
  methods: {
    roundChartValues(a) {
      a.data = a.data.map(
        item => {
          if(item && item.y) {
            item.y = Number(item.y).toFixed(2);
          }
        
          return item;
        }
      );
      return a;
    },
    sortByChartAvg(a,b) {
      const firstSortParamSum = this.sumOfChartPoints(a.data);
      const secondSortParamSum = this.sumOfChartPoints(b.data);

      const firstSortParamValidOccurencies = this.countChartValidPoints(a.data);
      const secondSortParamValidOccurencies = this.countChartValidPoints(b.data);

      const firstSortParamAvg = (firstSortParamSum/firstSortParamValidOccurencies);
      const secondSortParamAvg = (secondSortParamSum/secondSortParamValidOccurencies);
      
      return secondSortParamAvg - firstSortParamAvg;
    },
    sumOfChartPoints(arr) {
      return arr.reduce((i, j) => i + Number(j.y) , 0)
    },
    countChartValidPoints(arr) {
      return arr.filter(item => Number(item.y)).length;
    },
    dateFormatterChart (date) {
      return moment(date).lang('pt').format('DD / MMM') + '';
    }, 
    formatterTooltip (y,chart) {
      const maxStorage = this.managementHistoryData && this.managementHistoryData[0] && this.managementHistoryData[0].MAX_STORAGE || 1;
      let tooltipValue = y.toFixed(2);
      let basicValue = y.toFixed(2);
      let measurementUnity = '%';

      if (!chart.seriesIndex) {
        measurementUnity = 'mm'
      }

      if(chart.seriesIndex == 1 || chart.seriesIndex == 2) {
        basicValue = (tooltipValue * maxStorage)/100
      }

      if (typeof y !== "undefined") {
        return `${tooltipValue}${measurementUnity} ${measurementUnity === '%' && basicValue != tooltipValue ? ` (${basicValue.toFixed(2)}mm)` :''}`;
      }
      return y;

    }
  },
  computed: {
    fileName() {
      return `Manejo_${this.sectorName}_${moment().format("YYYY-MM-DD")}`;
    },
    maxMillimeterParam(){
      const sliceOfMaxStorage = (this.managementHistoryData[0].MAX_STORAGE / 4);
      const biggestValueExcess = Math.max(...this.managementHistoryData.map(s => s.EXCESS));
      
      return biggestValueExcess >  sliceOfMaxStorage ? biggestValueExcess : sliceOfMaxStorage; 
    },
    concatedSensorsValuesCleared(){
      const concatedData = [];

      for(const sensor of this.sensorHistoryData) {
        for(const sensorRead of sensor) {
          concatedData.push(sensorRead.VALUE);
        }
      }

      return concatedData.filter(val => val);
    },
    maxSensorParam(){
      const biggestSensorRead = Math.max(...this.concatedSensorsValuesCleared);
      const gapSensorRead = biggestSensorRead/5;

      if(!biggestSensorRead || biggestSensorRead + gapSensorRead > 100 ) {
        return 100;

      } else {
        return biggestSensorRead + gapSensorRead;

      }
    },
    minSensorParam(){
      return 0;
      // const minSensorRead = Math.min(...this.concatedSensorsValuesCleared);
      // const gapSensorRead = minSensorRead/5;


      // if(minSensorRead - gapSensorRead < 0 ) {
      //   return 0;

      // } else {
      //   return minSensorRead - gapSensorRead;

      // }
    },
    chartDataExcess() {
      return this.managementHistoryData.map(d => {
        return {
          x: d.TIME,
          y: d.EXCESS
        }
      })
    },
    chartDataAvailability() {
      return this.managementHistoryData.map(d => {
        return {
          x: d.TIME,
          y: ((1 - d.AVAILABILITY) * 100).toFixed(2)
        };
      })
    },
    chartDataFinalStorage() {
      return this.managementHistoryData.map(d => {
        return {
          x: d.TIME,
          y: (((d.FINAL_STORAGE)/d.MAX_STORAGE) * 100).toFixed(2) 
        }
      })
    },
    chartOptions() {
      return {
        chart: {
          type: 'line',
          stacked: false,
          toolbar: {
        show: true,
        offsetX: 0,
        offsetY: 0,
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true | '<img src="/static/icons/reset.png" width="20">',
          customIcons: []
        },
        export: {
          csv: {
            filename: this.fileName,
            columnDelimiter: ',',
            headerCategory: 'Dia',
            dateFormatter(timestamp) {
              return new Date(timestamp).toDateString();
            }
          },
          svg: {
            filename: this.fileName,
          },
          png: {
            filename: this.fileName,
          }
        },
        autoSelected: 'zoom' 
      },
        },
        stroke: {
          width: this.chartSeries.map((_, index) => index >= 3 ? 1 : 2),
          curve: 'straight',
          dashArray: [0, 10, 0]
        },
        fill: {
          opacity: 0.85,
          gradient: {
            inverseColors: false,
            shade: 'light',
            type: "vertical",
            opacityFrom: 0.85,
            opacityTo: 0.55,
          }
        },
        markers: {
          size: this.chartSeries.map((_, index) => index >= 3 ? 5 : 0)
        },
        xaxis: {
          labels: {
            formatter: this.dateFormatterChart
          }
        },
        yaxis: [
          ...this.chartSeries.map((s, index) => {
            if(!index) {
              return {
                seriesName: s.name,
                opposite: true,
                title: {
                    text: "Excedente (mm)"
                },
                min: 0,
                max: this.maxMillimeterParam,
              }
            } else if(index === 1) {
              return {
                seriesName: this.chartSeries[1].name,
                title: {
                    text: "Disp. de Água e Disp. Crítica (%)"
                },
                min: 0,
                max: 100
              }
            } else if(index == 2) {
              return {
                seriesName: this.chartSeries[1].name,
                show: false,
                min: 0,
                max: 100
              }
            } else if(index == 3) {
              return {
                seriesName: this.chartSeries[3].name,
                title: {
                    text: "Média Diária de Sensores (%)"
                },
                min: this.minSensorParam,
                max: this.maxSensorParam
              }
            } else {
              return {
                seriesName: this.chartSeries[3].name,
                show: false,
                min: this.minSensorParam,
                max: this.maxSensorParam
              }
            }
          })
        ],
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: this.formatterTooltip
          },
          x: {
            formatter: (index) => {
              return `Manejo ${moment(this.managementHistoryData[index - 1].TIME).lang('pt').format('DD / MMM')}`
            }
          }
        }
      }
    },
    chartSeries() {
      const sensorsCharts = this.sensorHistoryData
        .map((sensor, i) => {
          return {
            name: `Umidade do Solo - Sensor ${ sensor[0] && sensor[0].SENSOR_NAME } (%)`,
            type: 'line',
            data: sensor.map(d => {
              return {
                x: d.TIME,
                y: (d.VALUE || 0).toFixed(2)
              }
            })
          }
        })
        .sort(this.sortByChartAvg);
        
      return [
        {
          name: 'Excedente (mm)',
          type: 'column',
          color: 'rgb(255, 196, 96)',
          data: this.chartDataExcess,
        },
        {
          name: 'Disponibilidade Crítica (%)',
          type: 'line',
          color: 'rgba(255, 69, 96, 0.6)',
          data: this.chartDataAvailability,
        },
        {
          name: 'Disponibilidade de Água (%)',
          type: 'line',
          color: 'rgb(68, 69, 96)',
          data: this.chartDataFinalStorage
        },
        ...sensorsCharts
      ]
        .map(this.roundChartValues)
    }
  }
}
</script>
